import { type Evaluated, type EntitySource } from '@/domain/construction';
import { type Segment } from '@/domain/geometry';
import { type Vec2 } from '@/domain/math';
import { type EntityId, inDrawOrder, LineStyle, type Style } from '@/domain/model';
import {
  add3,
  buildNamedSolid,
  createProjector,
  type Piece,
  length3,
  occludeSegment,
  onPageOf,
  ringOf,
  scale3,
  subtract3,
  type Vec3,
  viewerDirection,
} from '@/domain/solid';
import { type Scene } from '@/engine';

/**
 * Which drawn things a solid covers.
 *
 * ## Why this is decided here and not once, when the figure was written
 *
 * Because it is a fact about the *view*, and the view can change. The first version worked it
 * out while the script ran and wrote the answer into each line's style, which is correct exactly
 * once: move or resize the solid and the shape follows while the dashes stay where they were.
 * ADR-0003 says a source records how an entity is determined and never what it currently looks
 * like, and a baked dash is precisely the second thing.
 *
 * Rendering is also simply where it belongs. Whether a line is covered is not a property of the
 * line — the same two points joined in the same order are hidden from one side and visible from
 * the other. `namePlacement` lives here for the same reason and works the same way: one pass
 * over the scene, one answer per entity, consumed identically by the canvas and by every export.
 *
 * ## Why every solid is asked
 *
 * A figure may hold more than one, and a point carries where it is in space without saying
 * which solid put it there. Testing against all of them needs no such bookkeeping and gives the
 * right answer anyway: a line inside one block is covered by that block and outside the other,
 * and a line running between two blocks is outside both.
 */
/**
 * What a solid covers, at two grains.
 *
 * A segment and an angle mark are each one thing with one answer. A polygon is not: `DEF` cut
 * through a tetrahedron has `EF` lying in a face the reader can see and `DF` running straight
 * through the middle, and one answer for the outline draws the second like the first. So a
 * polygon is answered edge by edge, and the painters stroke it edge by edge.
 */
export interface Covered {
  /** Entities the solid covers entirely. */
  readonly whole: ReadonlySet<EntityId>;
  /**
   * Segments a solid covers **in part**, as the stretches along them.
   *
   * The third grain, and the one that says a body is not made of glass *and* not a curtain. A
   * segment running out from behind a block was drawn entirely one way, decided by whichever
   * side of the halfway mark its middle fell — so `AM` from a corner to a point on the far face
   * flipped between solid and dashed as `M` was dragged, with nothing about the figure changing.
   *
   * Present only when the answer is genuinely mixed. A segment wholly covered is still in
   * {@link whole}, where it costs the painters nothing.
   */
  readonly parts: ReadonlyMap<EntityId, readonly Piece[]>;
  /** Per-edge answers, for entities whose outline is a chain. */
  readonly edges: ReadonlyMap<EntityId, readonly boolean[]>;
  /**
   * Edges a solid should not draw, because a segment of its own now does.
   *
   * "nhiều khi muốn 1 đoạn nào trong khối có style khác". Once an edge has been lifted out as a
   * segment — see `promoteActions` — the solid must let go of it, or a dashed red `SA` is drawn
   * over a solid black one showing through its gaps.
   *
   * Answered here, in the pass that already asks one question per entity for both the canvas
   * and every export, rather than written into the solid's own source. Nothing in the document
   * then refers to a line that might be deleted: remove the segment and the edge comes back by
   * itself, which is what a user undoing their way out of an experiment expects.
   */
  readonly lifted: ReadonlyMap<EntityId, readonly Segment[]>;
  /**
   * Sides of a ring shape another entity now draws, one answer per side.
   *
   * The same idea as {@link lifted} and a different shape of answer, because the two are drawn
   * differently: a solid is a bag of segments and drops the ones it has handed over, while a
   * polygon is a *ring* and is stroked side by side — so the answer it needs is one flag per
   * side, exactly like the hidden-line answers beside it.
   */
  readonly liftedEdges: ReadonlyMap<EntityId, readonly boolean[]>;
}

export function coveredSegments(scene: Scene): Covered {
  /** The solid occluders, findable by the entity that declared them. See `declaredSolid`. */
  const solidsById = new Map<
    EntityId,
    { occlude: (a: Vec3, b: Vec3) => readonly Piece[]; project: (at: Vec3) => Vec2 }
  >();
  const solids = [...scene.document.entities.values()].flatMap((entity) => {
    if (entity.source.op !== 'solid-figure') return [];
    const centre = scene.values.get(entity.source.center);
    if (centre?.kind !== 'point') return [];

    const built = buildNamedSolid(
      entity.source.solid,
      {
        at: centre.point,
        width: entity.source.width,
        height: entity.source.height,
        depth: entity.source.depth,
      },
      entity.source.corners,
      entity.source.foot,
      entity.source.baseAngleAt === undefined || entity.source.baseAngle === undefined
        ? undefined
        : {
            at: entity.source.baseAngleAt,
            degrees: entity.source.baseAngle,
            ...(entity.source.baseFar === undefined ? {} : { far: entity.source.baseFar }),
            ...(entity.source.baseFarOffset === undefined
              ? {}
              : { offset: entity.source.baseFarOffset }),
          },
      entity.source.baseCorners === undefined ? undefined : ringOf(entity.source.baseCorners),
      entity.source.sections
    );
    if (built === null) return [];
    const made = { occlude: built.occlude, project: built.project };
    solidsById.set(entity.id, made);
    return [made];
  });

  /*
   * Planes hide things too, and until now none of them did.
   *
   * A line running through a plane is drawn **part dashed, part solid** — dashed where it passes
   * behind the drawn parallelogram, solid where it is in front, meeting exactly at the point where
   * it pierces. That is how a textbook draws it, and drawing the whole line one way loses the one
   * thing the figure was made to show: which side of the plane each half is on.
   *
   * No new geometry was needed. `occludeSegment` works on **faces**, and a plane drawn as a
   * parallelogram is one face; `viewerDirection` already answers which way the reader is, from the
   * same projector the space is drawn through. So a plane joins the list of things that occlude,
   * and everything downstream — the cutting, the merging, the painters that stroke a segment in
   * pieces — is the machinery solids already use.
   *
   * A **section** of a solid is deliberately not here. It lies on the body's own surface, and the
   * body is already an occluder; adding the section as a second one would dash the very edges it
   * shares with the faces it was cut from.
   */
  const planes = [...scene.document.entities.values()].flatMap((entity) => {
    if (!entity.visible || entity.source.op !== 'plane-free') return [];

    const value = scene.values.get(entity.id);
    if (value?.kind !== 'polygon' || value.in3d === undefined || value.frame === undefined) {
      return [];
    }

    const space = scene.document.entities.get(entity.source.space)?.source;
    if (space?.op !== 'space') return [];

    const frameOfPlane = value.frame;
    /*
     * The viewing ray in **world** space, which is where these faces are.
     *
     * `viewerDirection` finds the direction a map sends to nothing, so it answers in whatever
     * space the map it is given starts from. Handed the bare projector it answers in *view*
     * space — the solid path wants that, because a solid's faces are already rotated — and the
     * first version of this took it at face value and got `(0, 0, 1)` for an isometric figure,
     * a ray pointing along an axis the drawing does not use. Nothing was ever behind anything.
     *
     * Given the frame instead, it answers in the space the plane's corners are written in. The
     * sign still has to come from the projector, which is the only thing that knows which end of
     * the ray the reader is at, and a rotation carries a direction into view space unchanged.
     */
    const projector = createProjector(space.projection, space.rotation, 1);
    const toViewer = viewerDirection({
      toPlane: (point: Vec3) => onPageOf(frameOfPlane, point),
      isFrontFacing: (normal: Vec3, centroid: Vec3) =>
        projector.isFrontFacing(projector.toView(normal), centroid),
    });
    // Perspective is not affine, so there is no single viewing ray to cut along. `space` refuses
    // it outright, which means this is unreachable; it stays because the day it becomes reachable
    // the answer must be "do not guess" rather than a dashed line in the wrong place.
    if (toViewer === null) return [];

    const face = value.in3d;
    const project = (point: Vec3) => onPageOf(frameOfPlane, point);

    return [
      {
        project,
        occlude: (a: Vec3, b: Vec3) => occludeSegment(a, b, [face], { project, toViewer }),
      },
    ];
  });

  const covered = new Set<EntityId>();
  const parts = new Map<EntityId, readonly Piece[]>();
  const edges = new Map<EntityId, readonly boolean[]>();
  const lifted = liftedEdges(scene);
  const liftedSides = liftedRingSides(scene);
  const occluders = [...solids, ...planes];
  if (occluders.length === 0) {
    return { whole: covered, parts, edges, lifted, liftedEdges: liftedSides };
  }

  /**
   * The one body a construction was written against, when it names one.
   *
   * A **plane** and everything derived from it carry the id of the solid they cut — `plane S A C`
   * is a statement about that pyramid and no other. Its section is a *computed* polygon with no
   * named corners, so the page-position test below cannot work out whose space it is in, and
   * without this it falls through to asking every body in the drawing.
   *
   * That fall-through was the whole cost of this pass. Measured on twenty-four pyramids each with
   * one section: **25.08 ms per frame**. The same figure with the sections deleted: **1.19 ms**.
   * Twenty-four entities out of two hundred and eighty-eight were doing twenty-one twenty-seconds
   * of the work, because each was tested against all twenty-four bodies instead of its own.
   *
   * The obvious suspect was rebuilding the meshes every frame, and it was measured and cleared
   * first: that is 0.39 ms of the 24 — under two per cent. Caching it would have bought nothing.
   *
   * Only the **solids** are narrowed. Planes occlude too now, and whether one plane hides another
   * figure's section is a question this change has no business answering.
   */
  const declaredSolid = (source: EntitySource) =>
    'solid' in source && typeof source.solid === 'string'
      ? solidsById.get(source.solid as EntityId)
      : undefined;

  const spaceOf = (id: EntityId): Vec3 | undefined => {
    const value = scene.values.get(id);
    return value?.kind === 'point' ? value.in3d : undefined;
  };

  /** Where a point is drawn, which is what says whose space its coordinates are in. */
  const pageOf = (id: EntityId): Vec2 | undefined => {
    const value = scene.values.get(id);
    return value?.kind === 'point' ? value.point : undefined;
  };

  /**
   * How far a solid's projection puts this point from where it is actually drawn.
   *
   * **Every solid's space is its own.** Two blocks a thousand apart on the page describe their
   * corners with the very same numbers — measured: two pyramids 600 apart both report their apex
   * at (0, 100, 0). So coordinates alone cannot say which body a point belongs to, and a solid
   * asked about another's point answers about a point of its own that happens to share them.
   *
   * That was not a limitation waiting to matter. Measured before the fix: a block drawn alone had
   * `AB` and `AA'` in plain view, and adding a **bigger block 1200 units away** dashed both — a
   * solid nowhere near it hid its edges, and the figure looked deliberate.
   *
   * Run a point through the right solid's projection and it lands where it is drawn; through any
   * other's it lands somewhere else, by exactly the distance between the two bodies. So this is
   * the whole test, and it needs nothing stored: the answer is already in the two numbers.
   */
  const ORIGIN_TOLERANCE = 1e-6;
  const belongs = (
    solid: { readonly project: (at: Vec3) => Vec2 },
    space: Vec3 | undefined,
    page: Vec2 | undefined
  ): boolean => {
    if (space === undefined || page === undefined) return false;
    const where = solid.project(space);
    return (
      Math.abs(where.x - page.x) < ORIGIN_TOLERANCE && Math.abs(where.y - page.y) < ORIGIN_TOLERANCE
    );
  };

  /** The solids whose own space both ends of this segment live in. Usually exactly one. */
  const asked = (ends: readonly [Vec3, Vec3], at: readonly [Vec2, Vec2]) =>
    occluders.filter((solid) => belongs(solid, ends[0], at[0]) && belongs(solid, ends[1], at[1]));

  for (const entity of inDrawOrder(scene.document)) {
    if (!entity.visible) continue;

    /*
     * A segment, however it was built.
     *
     * An ordinary one names its two ends, so they are looked up. A **giao tuyến** was computed —
     * it has no endpoints in the document at all — and carries its position in space on its
     * value instead. Both have to be asked, or `(SAC) ∩ (SBD)` comes out solid: the height of a
     * pyramid drawn as though it ran along the outside of the body.
     *
     * A line with no position in space is a line in the plane, and a plane figure has nothing in
     * front of it. Every ordinary flat construction takes that path and costs nothing.
     */
    const value = scene.values.get(entity.id);
    if (entity.source.op === 'segment' || value?.kind === 'segment') {
      const named = entity.source.op === 'segment';
      const ends = named
        ? [spaceOf(entity.source.a), spaceOf(entity.source.b)]
        : ((value?.kind === 'segment' ? value.in3d : undefined) ?? []);

      const [a, b] = ends;
      if (a === undefined || b === undefined) continue;

      /*
       * Only the solids this segment's own space belongs to.
       *
       * A **giao tuyến** was computed and names no endpoints, so there is nothing to look its
       * page position up from — those keep asking everyone, which is what they did before and is
       * right for now: a computed line already belongs to the solid that computed it.
       */
      const drawn = named
        ? [pageOf(entity.source.a), pageOf(entity.source.b)]
        : [undefined, undefined];
      const [pa, pb] = drawn;
      const home = declaredSolid(entity.source);
      const mine =
        home !== undefined
          ? [home, ...planes]
          : pa === undefined || pb === undefined
            ? occluders
            : asked([a, b], [pa, pb]);
      if (mine.length === 0 && pa !== undefined) continue;

      /*
       * Every solid asked, and their answers combined as a union of covered stretches.
       *
       * Not one call with all the faces: each solid's mesh is in its own local space, so two
       * blocks at different places on the page share the same coordinates and would be tested
       * against each other's geometry. Asking each in turn and merging the answers is both
       * correct and the only version that stays correct when a second solid is added.
       */
      const pieces = mergeCovered(mine.map((solid) => solid.occlude(a, b)));
      if (pieces.length > 0 && pieces.every((piece) => piece.hidden)) covered.add(entity.id);
      else if (pieces.length > 1) parts.set(entity.id, pieces);
      continue;
    }

    if (entity.source.op === 'angle-mark') {
      const probe = markProbe(
        spaceOf(entity.source.a),
        spaceOf(entity.source.vertex),
        spaceOf(entity.source.c)
      );
      if (probe === null) continue;

      // A mark is small and sits in a corner: it is behind the body or it is not.
      const home = asked(
        [probe.vertex, probe.into],
        [pageOf(entity.source.vertex) ?? { x: NaN, y: NaN }, { x: NaN, y: NaN }]
      );
      const seen = mergeCovered(
        (home.length > 0 ? home : solids).map((solid) => solid.occlude(probe.vertex, probe.into))
      );
      if (seen.length > 0 && seen.every((piece) => piece.hidden)) covered.add(entity.id);
      continue;
    }

    /*
     * A closed region, answered edge by edge.
     *
     * Its corners come from one of two places, and both are ordinary. A polygon the user wrote
     * names its corners, so they are looked up. A **plane's section** was computed, and carries
     * its own corners in space — which is the point of `in3d` on the value: the section of a
     * cube has two edges the reader can see and two running round the back, and one answer for
     * the whole outline would draw the second pair like the first.
     */
    const isPolygon = entity.source.op === 'polygon';
    const ring = isPolygon
      ? entity.source.vertices.map((id) => spaceOf(id))
      : ((value?.kind === 'polygon' ? value.in3d : undefined) ?? []);
    const ringPage = isPolygon
      ? entity.source.vertices.map((id) => pageOf(id))
      : ring.map(() => undefined);

    if (ring.length < 3 || ring.some((point) => point === undefined)) continue;

    const home = declaredSolid(entity.source);
    const answers = ring.map((point, index) => {
      const next = ring[(index + 1) % ring.length];
      if (point === undefined || next === undefined) return false;
      const from = ringPage[index];
      const to = ringPage[(index + 1) % ring.length];
      const mine =
        home !== undefined
          ? [home, ...planes]
          : from === undefined || to === undefined
            ? occluders
            : asked([point, next], [from, to]);
      if (mine.length === 0) return false;
      // A ring is stroked side by side, so a side is answered as a whole. Cutting one would need
      // the painters to stroke pieces of a side, which is a shape of answer nothing asks for yet.
      const pieces = mergeCovered(mine.map((solid) => solid.occlude(point, next)));
      return pieces.length > 0 && pieces.every((piece) => piece.hidden);
    });
    if (answers.some(Boolean)) edges.set(entity.id, answers);
  }

  return { whole: covered, parts, edges, lifted, liftedEdges: liftedSides };
}

/**
 * Several solids' answers about one segment, as a single covering of it.
 *
 * A stretch is covered when **any** body covers it, so what comes back is the union of the
 * hidden ranges — and the union has to be taken over parameters rather than by asking each body
 * for a verdict, because two blocks can each hide a different half of the same line.
 */
function mergeCovered(answers: readonly (readonly Piece[])[]): readonly Piece[] {
  const ranges: [number, number][] = [];
  for (const pieces of answers) {
    for (const piece of pieces) if (piece.hidden) ranges.push([piece.from, piece.to]);
  }
  if (ranges.length === 0) return [{ from: 0, to: 1, hidden: false }];

  ranges.sort((first, second) => first[0] - second[0]);
  const joined: [number, number][] = [];
  for (const range of ranges) {
    const last = joined[joined.length - 1];
    if (last !== undefined && range[0] <= last[1] + 1e-9) {
      last[1] = Math.max(last[1], range[1]);
      continue;
    }
    joined.push([...range]);
  }

  const out: Piece[] = [];
  let at = 0;
  for (const [from, to] of joined) {
    if (from - at > 1e-9) out.push({ from: at, to: from, hidden: false });
    out.push({ from: Math.max(at, from), to, hidden: true });
    at = to;
  }
  if (1 - at > 1e-9) out.push({ from: at, to: 1, hidden: false });
  return out;
}

/**
 * One drawn segment per stretch, so a partly covered line is two things to draw.
 *
 * Splitting the *drawable* rather than teaching the painters about stretches is what keeps this
 * to one rule: the canvas, the SVG and the PDF already know how to draw a segment that is dashed
 * or not, and a segment covered in part is genuinely two of those. Nothing downstream changes.
 */
export function cutIntoPieces(
  value: Evaluated,
  pieces: readonly Piece[] | undefined
): readonly { readonly value: Evaluated; readonly covered: boolean }[] {
  if (pieces === undefined || pieces.length < 2 || value.kind !== 'segment') {
    return [{ value, covered: false }];
  }

  const { a, b } = value.segment;
  const along = (t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });

  return pieces.map((piece) => ({
    value: { kind: 'segment', segment: { a: along(piece.from), b: along(piece.to) } },
    covered: piece.hidden,
  }));
}

/**
 * For each ring shape, which of its sides some other entity already draws.
 *
 * A user who lifts one side of a polygon out — to colour it, or to dash it — must not have the
 * polygon go on drawing it underneath: a dashed red side laid over a solid black one shows the
 * black through its gaps, which is the whole reason the same rule exists for a solid's edges.
 *
 * A side counts as handed over when a **visible** segment joins those two corners, in either
 * order. Invisible segments draw nothing, so a polygon must keep a side one of those covers, or
 * hiding the segment would put a gap in the ring.
 */
function liftedRingSides(scene: Scene): ReadonlyMap<EntityId, readonly boolean[]> {
  const joined = new Set<string>();
  for (const entity of scene.document.entities.values()) {
    if (!entity.visible || entity.source.op !== 'segment') continue;
    joined.add(pairKey(entity.source.a, entity.source.b));
  }
  if (joined.size === 0) return new Map();

  const result = new Map<EntityId, readonly boolean[]>();
  for (const entity of scene.document.entities.values()) {
    if (entity.source.op !== 'polygon') continue;

    const ring = entity.source.vertices;
    if (ring.length < 3) continue;

    const answers = ring.map((corner, index) =>
      joined.has(pairKey(corner, ring[(index + 1) % ring.length] ?? corner))
    );
    if (answers.some(Boolean)) result.set(entity.id, answers);
  }
  return result;
}

/** Two corners, in a fixed order, so a side is found however its segment was written. */
function pairKey(a: EntityId, b: EntityId): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

/**
 * The edges each solid has handed over to a segment of its own.
 *
 * A segment counts when both its ends are corners of the *same* solid and they are adjacent in
 * that solid's own numbering. Adjacency matters: `AC` across the face of a cube is a diagonal
 * the user drew, not an edge the cube was going to draw anyway, and the cube must keep every
 * line it had.
 */
function liftedEdges(scene: Scene): ReadonlyMap<EntityId, readonly Segment[]> {
  const result = new Map<EntityId, Segment[]>();

  /** The solid and corner a point names, when it is a corner of one. */
  const cornerOf = (id: EntityId): { solid: EntityId; index: number } | null => {
    const source = scene.document.entities.get(id)?.source;
    return source?.op === 'solid-vertex' ? { solid: source.solid, index: source.index } : null;
  };

  for (const entity of scene.document.entities.values()) {
    if (!entity.visible || entity.source.op !== 'segment') continue;

    const a = cornerOf(entity.source.a);
    const b = cornerOf(entity.source.b);
    if (a === null || a.solid !== b?.solid) continue;

    const solid = scene.values.get(a.solid);
    if (solid?.kind !== 'solid') continue;

    const joined = (solid.edges ?? []).some(
      ([from, to]) => (from === a.index && to === b.index) || (from === b.index && to === a.index)
    );
    if (!joined) continue;

    const from = solid.points?.[a.index];
    const to = solid.points?.[b.index];
    if (from === undefined || to === undefined) continue;

    const list = result.get(a.solid) ?? [];
    list.push({ a: from, b: to });
    result.set(a.solid, list);
  }

  return result;
}

/** How near two endpoints must be to count as the same, in world units. */
const SAME = 1e-6;

/**
 * The same solid, without the edges something else now draws.
 *
 * Applied where a figure is *drawn* and nowhere else. `scene.values` keeps the whole solid, so
 * its bounds still enclose it and a click anywhere along it still finds it — a lifted edge does
 * not punch a hole in the object, it only stops being drawn twice.
 */
export function withoutLiftedEdges(value: Evaluated, lifted: readonly Segment[]): Evaluated {
  if (value.kind !== 'solid' || lifted.length === 0) return value;

  const same = (one: Vec2, other: Vec2): boolean =>
    Math.abs(one.x - other.x) < SAME && Math.abs(one.y - other.y) < SAME;

  const handedOver = (edge: Segment): boolean =>
    lifted.some(
      (gone) =>
        (same(edge.a, gone.a) && same(edge.b, gone.b)) ||
        (same(edge.a, gone.b) && same(edge.b, gone.a))
    );

  const keep = (list: readonly Segment[]): readonly Segment[] => list.filter((e) => !handedOver(e));

  return {
    ...value,
    visible: keep(value.visible),
    hidden: keep(value.hidden),
    ...(value.straight === undefined
      ? {}
      : {
          straight: { visible: keep(value.straight.visible), hidden: keep(value.straight.hidden) },
        }),
  };
}

/**
 * Where in space an angle mark actually sits, as a segment the occlusion test can be asked about.
 *
 * A mark is not at its vertex — it is the small region *between* the two arms, and that is what
 * can be inside a solid. The angle α at A between AS and AO in a cone opens straight into the
 * body of the cone, so the arc is behind two sheets of it while the vertex A is on the rim, in
 * plain view. Asking about the vertex would answer the wrong question.
 *
 * So the probe runs from the vertex along the bisector, and the occlusion test — which asks about
 * a segment's midpoint — lands squarely in the middle of the arc. The length is a quarter of the
 * shorter arm rather than the drawn radius, because the drawn radius is in screen pixels and this
 * is a question about space; any short length gives the same answer, and a relative one cannot be
 * thrown off by a figure drawn at a different scale.
 *
 * `null` when there is nothing to ask: a mark whose points are not on a solid, or one whose arms
 * are opposite, where the bisector is the zero vector and the "inside of the corner" is not a
 * place.
 */
function markProbe(
  a: Vec3 | undefined,
  vertex: Vec3 | undefined,
  c: Vec3 | undefined
): { vertex: Vec3; into: Vec3 } | null {
  if (a === undefined || vertex === undefined || c === undefined) return null;

  const toA = subtract3(a, vertex);
  const toC = subtract3(c, vertex);
  const armA = length3(toA);
  const armC = length3(toC);
  if (armA === 0 || armC === 0) return null;

  const bisector = add3(scale3(toA, 1 / armA), scale3(toC, 1 / armC));
  const spread = length3(bisector);
  if (spread < 1e-9) return null;

  const reach = 0.25 * Math.min(armA, armC);
  return { vertex, into: add3(vertex, scale3(bisector, reach / spread)) };
}

/**
 * The style a line is actually drawn in, once the solid has had its say.
 *
 * Only the dash changes. The tone stays the author's, so a line they emphasised and the block
 * covers comes out dark *and* dashed — which is the honest drawing of "this is what the question
 * is about, and it runs through the middle".
 */
export function dashedIfCovered(style: Style, covered: boolean | undefined): Style {
  return covered === true ? { ...style, lineStyle: LineStyle.Dashed } : style;
}
